import { Box, Button, Typography, } from '@mui/material';
import { AnimatePresence, motion, useScroll, useSpring } from 'framer-motion';
import { useEffect, useMemo, useRef } from 'react';
import { Container } from '@mui/system';
import { useNavigate } from 'react-router-dom';
import HeroKurumsal from '../components/HeroKurumsal';
import FeatureHighlightsKurumsalNew from '../components/FeatureHighlightsKurumsalNew';
import OfficialInfrastructureSectionKurumsal from '../components/OfficialInfrastructureSectionKurumsal';
import SuperInfrastructureSectionKurumsal from '../components/SuperInfrastructureSectionKurumsal';
import ModuleSystemSectionKurumsal from '../components/ModuleSystemSectionKurumsal';
import WidgetFreedomSectionKurumsal from '../components/WidgetFreedomSectionKurumsal';
import HowItWorksSectionKurumsal from '../components/HowItWorksSectionKurumsal';
import MobileAppSectionKurumsal from '../components/MobileAppSectionKurumsal';
import WhoIsItForSectionKurumsal from '../components/WhoIsItForSectionKurumsal';
import { EndSectionKurumsal } from '../components/EndSectionKurumsal';

const HomePageKurumsal = () => {
  const navigate = useNavigate();
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });
  const sectionRefs = useRef([]);
  const heroKurumsalRef = useRef();
  const featureKurumsalHighlightsRef = useRef();
  const widgetSystemKurumsalSectionRef = useRef();
  const eSignatureFeaturesRef = useRef();
  const contractWorkflowRef = useRef();
  const mobileAppSectionRef = useRef();
  const securityComplianceSectionRef = useRef();
  const officialInfrastructureKurumsalRef = useRef();
  const superInfrastructureKurumsalRef = useRef();


  const sections = useMemo(() => [
    { component: HeroKurumsal, ref: heroKurumsalRef },
    { component: FeatureHighlightsKurumsalNew, ref: featureKurumsalHighlightsRef },
    { component: OfficialInfrastructureSectionKurumsal, ref: officialInfrastructureKurumsalRef },
    { component: SuperInfrastructureSectionKurumsal, ref: superInfrastructureKurumsalRef },
    { component: ModuleSystemSectionKurumsal, ref: widgetSystemKurumsalSectionRef },
    { component: WidgetFreedomSectionKurumsal, ref: eSignatureFeaturesRef },
    { component: HowItWorksSectionKurumsal, ref: contractWorkflowRef },
    { component: MobileAppSectionKurumsal, ref: mobileAppSectionRef },
    { component: WhoIsItForSectionKurumsal, ref: securityComplianceSectionRef },
    { component: EndSectionKurumsal, ref: securityComplianceSectionRef },
  ], []);

  useEffect(() => {
    sectionRefs.current = sections.map(section => section.ref.current);
  }, [sections]);

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Progress Bar */}
      <motion.div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: '#635BFF',
          transformOrigin: '0%',
          scaleX,
          zIndex: 1000,
        }}
      />

      <AnimatePresence mode="wait">
        <motion.div>
          {sections.map(({ component: Component }, index) => (
            <motion.div
              key={index}
              ref={sections[index].ref}
              viewport={{ once: true, margin: "-20%" }}
            >
              <Component />
            </motion.div>
          ))}
        </motion.div>
      </AnimatePresence>
        <Box
        sx={{
          py: { xs: 10, md: 16 },
          background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 50%, #f0f9ff 100%)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Dekoratif arka plan - multiple layers */}
        <Box
          sx={{
            position: 'absolute',
            top: '-10%',
            right: '10%',
            width: 500,
            height: 500,
            background: 'radial-gradient(circle, rgba(8, 145, 178, 0.15) 0%, transparent 70%)',
            borderRadius: '50%',
            filter: 'blur(60px)',
            pointerEvents: 'none',
            animation: 'float 8s ease-in-out infinite',
            '@keyframes float': {
              '0%, 100%': { transform: 'translateY(0px)' },
              '50%': { transform: 'translateY(-20px)' },
            },
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '-10%',
            left: '5%',
            width: 400,
            height: 400,
            background: 'radial-gradient(circle, rgba(16, 185, 129, 0.12) 0%, transparent 70%)',
            borderRadius: '50%',
            filter: 'blur(50px)',
            pointerEvents: 'none',
            animation: 'float 6s ease-in-out infinite',
            animationDelay: '1s',
          }}
        />

        {/* Decorative shapes */}
        <Box
          sx={{
            position: 'absolute',
            top: '20%',
            left: '10%',
            width: 100,
            height: 100,
            border: '2px solid rgba(8, 145, 178, 0.1)',
            borderRadius: '20px',
            transform: 'rotate(15deg)',
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '25%',
            right: '15%',
            width: 80,
            height: 80,
            background: 'rgba(16, 185, 129, 0.05)',
            borderRadius: '50%',
            pointerEvents: 'none',
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <Box
              sx={{
                background: 'rgba(255, 255, 255, 0.7)',
                textAlign: 'center',
                p: { xs: 6, md: 10 },
                borderRadius: 5,
                border: '1px solid rgba(8, 145, 178, 0.2)',
                backdropFilter: 'blur(20px)',
                boxShadow: '0 20px 60px rgba(8, 145, 178, 0.1), 0 0 0 1px rgba(255,255,255,0.8) inset',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(8, 145, 178, 0.05), transparent)',
                  animation: 'shimmer 3s infinite',
                },
                '@keyframes shimmer': {
                  '0%': { left: '-100%' },
                  '100%': { left: '100%' },
                },
              }}
            >
              <Typography
                sx={{
                  fontWeight: 800,
                  mb: 2,
                  color: '#0f172a',
                  fontSize: { xs: '2rem', md: '2.8rem' },
                  lineHeight: 1.2,
                  letterSpacing: '-0.02em',
                }}
              >
                Derneğinizi Yönetimini Dijitale Taşıyın
              </Typography>
              <Typography
                sx={{
                  mb: 5,
                  color: '#475569',
                  fontSize: { xs: '1.05rem', md: '1.2rem' },
                  lineHeight: 1.6,
                  maxWidth: 600,
                  mx: 'auto',
                  fontWeight: 500,
                }}
              >
                Web sitenizi yönetin, üyelerinizle iletişim kurun ve tüm dernek süreçlerini
                tek panelden kontrol edin. 
                <br/>
                DernekSistem ile yönetim artık çok daha kolay.

              </Typography>
              <Box sx={{ display: 'flex', gap: 2.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => navigate('/iletisim')}
                    sx={{
                      background: 'linear-gradient(135deg, #ff8305 17%, #06C4FE 104%)',
                      color: '#ffffff',
                      fontWeight: 700,
                      px: 6,
                      py: 2.5,
                      fontSize: '1rem',
                      textTransform: 'none',
                      borderRadius: 3,
                      boxShadow: '0 10px 30px rgba(8, 145, 178, 0.3)',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, transparent 100%)',
                        opacity: 0,
                        transition: 'opacity 0.3s ease',
                      },
                      '&:hover': {
                        boxShadow: '0 15px 40px rgba(8, 145, 178, 0.4)',
                        '&::before': {
                          opacity: 1,
                        },
                      },
                    }}
                  >
                    Hemen Başlayın
                  </Button>
                </motion.div>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => navigate('/ucretlendirme')}
                    sx={{
                      borderColor: 'rgba(8, 145, 178, 0.3)',
                      color: '#0891b2',
                      textTransform: 'none',
                      fontWeight: 700,
                      fontSize: '1rem',
                      px: 6,
                      py: 2.5,
                      borderRadius: 3,
                      borderWidth: 2,
                      backgroundColor: 'rgba(255,255,255,0.5)',
                      backdropFilter: 'blur(10px)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: 'rgba(8, 145, 178, 0.08)',
                        borderColor: '#0891b2',
                        borderWidth: 2,
                      },
                    }}
                  >
                    Fiyatları İncele
                  </Button>
                </motion.div>
              </Box>
            </Box>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default HomePageKurumsal;